import React, { useState, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { Box, Typography, Tab } from '@mui/material';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import { GET_PET_PHOTOS } from '../../queries';
import { useAwoContext } from '../AwoContextProvider';
import withProfile from '../withProfile';
import { PetPhotosResponse } from '../../types/PetPhoto';
import MediaDialog from '../MediaDialog';
import RenderTabPanel from './components/TabPanel';

interface PetPhotosProps {
  setDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setUserId: React.Dispatch<React.SetStateAction<number | null>>;
}

const CARDS_PER_PAGE = 12;

function PetPhotos({ setDrawerOpen, setUserId }: PetPhotosProps) {
  const { awo } = useAwoContext();

  // Determine which care types are available
  const hasAdopted = awo?.dogAdopt || awo?.catAdopt;
  const hasFoster = awo?.dogFoster || awo?.catFoster || awo?.kittenFoster;

  const [careType, setCareType] = useState('Adopted'); // Default to Adopted initially
  const [cardsOffset, setCardsOffset] = useState(0);

  // Update care type when awo data becomes available
  useEffect(() => {
    // Set initial care type based on what's available
    const getInitialCareType = () => {
      if (hasAdopted) return 'Adopted';
      if (hasFoster) return 'Foster';
      return 'Adopted'; // fallback
    };
    if (awo) {
      setCareType(getInitialCareType());
    }
  }, [awo, hasAdopted, hasFoster]);

  const [dialogState, setDialogState] = useState({
    open: false,
    img: '',
    date: '',
    petName: '',
  });

  const {
    error,
    data,
    loading,
    fetchMore,
  } = useQuery<{ petPhotos: PetPhotosResponse }>(GET_PET_PHOTOS, {
    variables: {
      awoId: awo.id ? parseInt(awo.id, 10) : null,
      careType,
      limit: CARDS_PER_PAGE,
      offset: 0,
    },
    skip: !awo.id,
  });

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setCardsOffset(0);
    setCareType(newValue);
  };

  const handleLoadMore = () => {
    const newOffset = cardsOffset + CARDS_PER_PAGE;
    setCardsOffset(newOffset);

    fetchMore({
      variables: {
        awoId: awo.id ? parseInt(awo.id, 10) : null,
        careType,
        offset: newOffset,
        limit: CARDS_PER_PAGE,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;

        return {
          petPhotos: {
            photos: [
              ...prev.petPhotos.photos,
              ...fetchMoreResult.petPhotos.photos,
            ],
            totalCount: fetchMoreResult.petPhotos.totalCount,
            hasMore: fetchMoreResult.petPhotos.hasMore,
          },
        };
      },
    });
  };

  const openMediaDialog = (img: string, date: string, name?: string) => {
    setDialogState({
      open: true,
      img,
      date,
      petName: name || '',
    });
  };

  if (error) return null;

  return (
    <Box sx={{
      height: '100vh',
      position: 'relative',
      overflow: 'auto',
    }}
    >
      <TabContext value={careType}>
        <Box
          sx={{
            paddingTop: 2,
            paddingBottom: 0,
            paddingLeft: 3,
            paddingRight: 0,
            position: 'sticky',
            background: 'white',
            top: 0,
            zIndex: 1,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="overline">Photos</Typography>
          <Box>
            <TabList onChange={handleTabChange} aria-label="photo categories">
              {hasAdopted && (
                <Tab label="Adopter Photos" value="Adopted" />
              )}
              {hasFoster && (
                <Tab label="Foster Photos" value="Foster" />
              )}
            </TabList>
          </Box>
        </Box>

        {hasAdopted && (
          <RenderTabPanel
            value="Adopted"
            photos={data?.petPhotos?.photos || []}
            loading={loading}
            hasMore={data?.petPhotos?.hasMore || false}
            onLoadMore={handleLoadMore}
            openMediaDialog={openMediaDialog}
            setDrawerOpen={setDrawerOpen}
            setUserId={setUserId}
          />
        )}

        {hasFoster && (
          <RenderTabPanel
            value="Foster"
            photos={data?.petPhotos?.photos || []}
            loading={loading}
            hasMore={data?.petPhotos?.hasMore || false}
            onLoadMore={handleLoadMore}
            openMediaDialog={openMediaDialog}
            setDrawerOpen={setDrawerOpen}
            setUserId={setUserId}
          />
        )}
      </TabContext>

      <MediaDialog
        dialogImg={dialogState.img}
        dialogDate={dialogState.date}
        imgDialogOpen={dialogState.open}
        setImgDialogOpen={(open) => setDialogState((prev) => ({ ...prev, open }))}
        petName={dialogState.petName}
      />
    </Box>
  );
}

export default withProfile(PetPhotos);
